import { idTypeLabel } from '@/domains/auth';
import type { ISearchMatch } from './api/participant-contracts';

/**
 * Why a search row matched, in words — survey-api's `matches` (record search §6, SR-414) — and
 * the words of `q` marked in a matched value. Two halves of different standing.
 *
 * **`MATCH_FIELD_WORDS` is words, not a mirror.** survey-api's `SearchMatchFields.All` names the
 * fields; what each is called on a screen is this side's. A field it lacks is drawn as spelled,
 * never dropped — `field` is an open set on the wire. The four roster fields name the team or
 * firm the slot is on, which the caller draws after the words (a link, with the slot's words):
 * "Name on the roster of" *The Roberts Group*.
 *
 * **`markedWords` is a mirror** of survey-api's `SearchTerms` (`Parse`, `MustStartWord`) and of
 * how `RecordSearch` holds a word in a value — the `COLUMN_DEFAULTS` precedent: it moves with
 * `SearchTerms.cs`. `q` is split on whitespace and commas, repeats dropped ignoring case; a word
 * of fewer than three characters, or of digits only and fewer than seven, matches only where a
 * word starts — at the value's start or after a space, as `(' ' || text) ILIKE '% word%'` reads
 * it, so punctuation starts no word — and any other word anywhere, ignoring case. A match of rank
 * 0 or 1 (an identifier, a record id, an exact email — record search §5.4) is marked whole where
 * it is the needle, or where none of its words can be marked (the needle in another spelling);
 * survey-api ranks the record, not the column, so another value of the record at that rank is
 * marked by its words. **It is display only**: it decides no row, ranks
 * nothing and is never asked whether a value matched — a value with nothing to mark is drawn plain.
 */

/** Each field's words. `identifier` is named by its type and `notes` / `forbes_id` by their
 *  field's label (`matchFieldWords`), so their entries here are the fallback. */
export const MATCH_FIELD_WORDS: Readonly<Record<string, string>> = {
  display_name: 'Name',
  first_name: 'First name',
  last_name: 'Last name',
  preferred_name: 'Preferred name',
  legal_firm_name: 'Legal firm name',
  firm_name: 'Firm name',
  team_name: 'Team name',
  contact_name: 'Contact name',
  city: 'City',
  state: 'State',
  postal_code: 'Zip code',
  office_email: 'Office email',
  alt_email: 'Alternate email',
  cc_email: 'CC email',
  phone: 'Phone',
  cell_phone: 'Cell phone',
  account_email: 'Login email',
  email: 'Email',
  identifier: 'Number',
  salesforce_id: 'Salesforce id',
  record_id: 'Record id',
  forbes_id: 'Forbes ID',
  legal_name: 'Legal name',
  business_name: 'Business name',
  sec_number: 'SEC number',
  firm_crd: 'Firm CRD',
  member_name: 'Name on the roster of',
  member_email: 'Email on the roster of',
  member_crd: 'CRD on the roster of',
  member_of: 'Member of',
  notes: 'Staff field',
  staff_note: 'Staff note',
  task: 'Salesforce task',
  work_item: 'Work item'
};

/** The four fields a roster slot carries — drawn with the team or firm the slot is on. */
export const ROSTER_MATCH_FIELDS: ReadonlySet<string> = new Set([
  'member_name',
  'member_email',
  'member_crd',
  'member_of'
]);

/** A match's label: an identifier by its type, a staff field's value by the field's name, any
 *  other field by `MATCH_FIELD_WORDS`, or as spelled. */
export function matchFieldWords(match: ISearchMatch): string {
  if (match.field === 'identifier' && match.idType) return idTypeLabel(match.idType);
  if ((match.field === 'notes' || match.field === 'forbes_id') && match.source) {
    return match.source.label;
  }
  return MATCH_FIELD_WORDS[match.field] ?? match.field;
}

/** One run of a value, marked or not. */
export interface IMarkedPart {
  text: string;
  marked: boolean;
}

/** `SearchTerms`' `ShortWord`: a word shorter than a trigram. */
const SHORT_WORD = 3;

/** `SearchTerms`' `PhoneDigits`: a number shorter than a local phone number. */
const PHONE_DIGITS = 7;

/** `SearchTerms.Words`: `q` split on whitespace and commas, without empty or repeated words
 *  (ignoring case), the first spelling kept. */
export function searchWords(q: string): string[] {
  const seen = new Set<string>();
  const words: string[] = [];
  for (const word of q.trim().split(/[\s,]+/u)) {
    const folded = word.toLowerCase();
    if (word === '' || seen.has(folded)) continue;
    seen.add(folded);
    words.push(word);
  }
  return words;
}

/** `SearchTerms.MustStartWord`. */
export function mustStartWord(word: string): boolean {
  return (
    word.length > 0 &&
    (word.length < SHORT_WORD || (word.length < PHONE_DIGITS && /^[0-9]+$/u.test(word)))
  );
}

/** A word as a pattern matching itself, ignoring case. */
function patternOf(word: string): RegExp {
  return new RegExp(word.replace(/[.*+?^${}()|[\]\\]/gu, '\\$&'), 'giu');
}

/** A value or a search as one comparable string — trimmed, its spaces collapsed, case dropped. */
function folded(text: string): string {
  return text.trim().replace(/\s+/gu, ' ').toLowerCase();
}

/**
 * `value` in runs, the words of `q` marked where survey-api's rule holds them (see the module's
 * comment). A match of rank 0 or 1 is marked whole only where it is the search itself, or where
 * no word of it can be marked — a number found in another spelling: survey-api ranks a record,
 * not a column, so a second email or identifier of the same record arrives at that rank too and
 * is marked by its words (gate 2). Overlapping marks are merged.
 */
export function markedWords(value: string, q: string, rank?: number): IMarkedPart[] {
  if (value === '') return [];
  const exact = rank !== undefined && rank <= 1;
  if (exact && folded(value) === folded(q)) return [{ text: value, marked: true }];

  const marks: Array<[number, number]> = [];
  for (const word of searchWords(q)) {
    const startOnly = mustStartWord(word);
    for (const hit of value.matchAll(patternOf(word))) {
      const at = hit.index ?? 0;
      if (startOnly && at > 0 && value[at - 1] !== ' ') continue;
      marks.push([at, at + hit[0].length]);
    }
  }
  if (marks.length === 0) return [{ text: value, marked: exact }];

  marks.sort((a, b) => a[0] - b[0]);
  const parts: IMarkedPart[] = [];
  let end = 0;
  for (const [from, to] of marks) {
    if (to <= end) continue;
    const start = Math.max(from, end);
    if (start > end) parts.push({ text: value.slice(end, start), marked: false });
    const last = parts[parts.length - 1];
    if (last?.marked && start === end) last.text += value.slice(start, to);
    else parts.push({ text: value.slice(start, to), marked: true });
    end = to;
  }
  if (end < value.length) parts.push({ text: value.slice(end), marked: false });
  return parts;
}
